import { useState, useCallback } from 'react';

const ROOT_CONTEXT = { id: 'root', label: 'System Context', elementId: null };

/** C4 kinds that can be drilled into, mapped to the view label shown in breadcrumb */
export const DRILLABLE_KINDS = {
  system: 'Container View',
  'external-system': 'Container View',
  container: 'Component View',
  webapp: 'Component View',
  database: 'Component View',
  microservice: 'Component View',
  serverless: 'Component View',
  queue: 'Component View',
  mobileapp: 'Component View',
  filestore: 'Component View',
};

// ─── View-level kind visibility rules ────────────────────────────────────────

/**
 * System Context View: only persons and whole software systems.
 * Containers and components must not appear here.
 */
const CONTEXT_VIEW_KINDS = new Set([
  'person', 'external-person', 'system', 'external-system',
]);

/**
 * Container View: the focused system's internal containers.
 */
const CONTAINER_VIEW_INNER_KINDS = new Set([
  'container', 'database', 'queue', 'webapp', 'mobileapp',
  'filestore', 'microservice', 'serverless',
]);

/**
 * External references shown as closed black-boxes in Container View.
 * Only the top-level system shape is shown — their containers stay hidden.
 */
const CONTAINER_VIEW_EXTERNAL_KINDS = new Set(['system', 'external-system']);

/**
 * Component View: the focused container's internal components.
 */
const COMPONENT_VIEW_INNER_KINDS = new Set([
  'component', 'controller', 'repository', 'service', 'facade',
]);

/**
 * External references shown as closed black-boxes in Component View:
 * sibling containers of the same system + top-level external systems.
 */
const COMPONENT_VIEW_EXTERNAL_KINDS = new Set([
  'container', 'database', 'queue', 'webapp', 'mobileapp',
  'filestore', 'microservice', 'serverless',
  'system', 'external-system',
]);

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useC4Navigation = () => {
  const [contextStack, setContextStack] = useState([ROOT_CONTEXT]);
  const currentContext = contextStack[contextStack.length - 1];

  const canDrillDown = useCallback((element) => {
    if (!element?.c4?.kind) return false;
    return !!DRILLABLE_KINDS[element.c4.kind];
  }, []);

  /** Drill into a C4 element, using allElements to find its label text. */
  const drillDown = useCallback((element, allElements) => {
    const viewLabel = DRILLABLE_KINDS[element.c4?.kind];
    if (!viewLabel) return;

    const textEl = allElements?.find(
      (el) => el.type === 'text' && el.attachedToElement === element.id
    );
    const rawLabel = textEl
      ? (textEl.content || '').split('\n')[0]
      : (element.defaultLabel || element.c4?.kind || '');
    const label = rawLabel || element.c4?.kind || 'Element';

    setContextStack((prev) => [
      ...prev,
      { id: element.id, label, elementId: element.id, viewLabel },
    ]);
  }, []);

  const goBack = useCallback(() => {
    setContextStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const jumpTo = useCallback((contextId) => {
    setContextStack((prev) => {
      const idx = prev.findIndex((c) => c.id === contextId);
      if (idx < 0) return prev;
      return prev.slice(0, idx + 1);
    });
  }, []);

  const resetNavigation = useCallback(() => setContextStack([ROOT_CONTEXT]), []);

  /**
   * Filter elements for the current view, enforcing C4 visibility rules:
   *
   * System Context View  → persons + whole systems only (no containers/components)
   * Container View       → focused system's containers + external systems as black-boxes
   * Component View       → focused container's components + sibling containers/systems as closed deps
   *
   * Attached texts and arrow labels follow their parent elements.
   * Arrows whose connected endpoints are outside the visible set are hidden.
   */
  const filterForContext = useCallback((elements, context) => {
    const contextId = context.elementId;     // null = root
    const viewLabel = context.viewLabel;     // 'Container View' | 'Component View' | undefined
    const isRoot = contextId === null;

    // Determine kind-sets for this view level
    let innerKinds;    // kinds allowed when contextId matches
    let externalKinds; // root-level kinds shown as closed references

    if (isRoot) {
      innerKinds = CONTEXT_VIEW_KINDS;
      externalKinds = null;
    } else if (viewLabel === 'Container View') {
      innerKinds = CONTAINER_VIEW_INNER_KINDS;
      externalKinds = CONTAINER_VIEW_EXTERNAL_KINDS;
    } else if (viewLabel === 'Component View') {
      innerKinds = COMPONENT_VIEW_INNER_KINDS;
      externalKinds = COMPONENT_VIEW_EXTERNAL_KINDS;
    } else {
      innerKinds = null;
      externalKinds = null;
    }

    const isC4Rect = (el) => el.type === 'rectangle' && el.c4?.kind;

    /** True if el belongs to the current context and passes the kind filter. */
    const isInContext = (el) => {
      if ((el.c4?.contextId ?? null) !== contextId) return false;
      if (!isC4Rect(el)) return true;        // non-C4 shapes: pass through if in context
      if (!innerKinds) return true;           // no filter configured
      return innerKinds.has(el.c4.kind);
    };

    // For sub-views, find the focused element's own parent context so we can
    // prevent the grandparent system/container from appearing as an external ref
    // (it was shown as a boundary box at the level above, not here).
    const focusedEl = !isRoot ? elements.find(e => e.id === contextId) : null;
    const parentContextId = focusedEl?.c4?.contextId ?? null;

    /**
     * True if el is a root-level element eligible to appear as an external
     * reference (black-box / closed dependency) in the current sub-view.
     * The focused element itself and its direct parent are excluded — they are
     * represented by dashed boundary boxes at their respective levels.
     */
    const isExternalRef = (el) => {
      if (isRoot || !externalKinds) return false;
      if (!isC4Rect(el)) return false;
      if ((el.c4?.contextId ?? null) !== null) return false; // must be root-level
      if (el.id === contextId) return false;         // focused element → boundary box
      if (el.id === parentContextId) return false;   // parent system → hidden in this view
      return externalKinds.has(el.c4.kind);
    };

    // --- Primary (non-text, non-arrow) elements ---
    const primary = elements.filter((el) => {
      if (el.type === 'text' && (el.attachedToElement || el.attachedToArrow)) return false;
      if (el.type === 'arrow') return false; // handled separately
      return isInContext(el) || isExternalRef(el);
    });

    const primaryIds = new Set(primary.map((e) => e.id));

    // --- Attached texts: follow their parent element ---
    const attachedTexts = elements.filter(
      (el) => el.type === 'text' && el.attachedToElement && primaryIds.has(el.attachedToElement)
    );

    // --- Arrows: must be in context; hide if a connected endpoint is not visible ---
    const arrows = elements.filter((el) => {
      if (el.type !== 'arrow') return false;
      if ((el.c4?.contextId ?? null) !== contextId) return false;
      // If an arrow endpoint is connected to a specific element, that element must be visible
      const startId = el.startConnection?.elementId;
      const endId = el.endConnection?.elementId;
      if (startId && !primaryIds.has(startId)) return false;
      if (endId && !primaryIds.has(endId)) return false;
      return true;
    });

    const arrowIds = new Set(arrows.map((a) => a.id));

    // --- Arrow labels: follow their arrow ---
    const arrowLabels = elements.filter(
      (el) => el.type === 'text' && el.attachedToArrow && arrowIds.has(el.attachedToArrow)
    );

    return [...primary, ...attachedTexts, ...arrows, ...arrowLabels];
  }, []);

  /**
   * Tag an element (or array of elements) with the given context id.
   * Merges into existing c4 metadata so other C4 fields are preserved.
   */
  const tagWithContext = useCallback((elementOrArray, contextId) => {
    const tag = (el) => ({ ...el, c4: { ...(el.c4 || {}), contextId } });
    if (Array.isArray(elementOrArray)) return elementOrArray.map(tag);
    return tag(elementOrArray);
  }, []);

  return {
    contextStack,
    currentContext,
    canDrillDown,
    drillDown,
    goBack,
    jumpTo,
    resetNavigation,
    filterForContext,
    tagWithContext,
  };
};
